import { Injectable } from '@angular/core';
import { User as Auth0User } from '@auth0/auth0-spa-js/dist/typings/global';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, groupBy, mergeMap, take } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';

import { WindowReferenceService } from './window-reference.service';
import { ProjectManagementService } from '../../home/services/project-management.service';
import {
  AddToCartAnalytics,
  AddToCta, AddToCtaExternalLink, AddToCtaOnEnclosureActionTrigger, AddToCtaOnProjectActionTrigger,
  AddToLoginAnalytics, AddToSearch,
  AddToSearchResultClicked,
  DebouncedSearchPayload,
  DownloadAnalyticsExcelFile,
  DownloadAnalyticsPdfFile,
  DownloadAnalyticsWordFile,
  DownloadAnalyticsZipFile,
  PageViewAnalytics,
  SITE_SEARCH_CATEGORY, ViewItemListAnalytics
} from '../models/analytics';
import { IBomModel } from '../../prisma-xs/models/bom.model';
import { mapToEcommerceItems } from '../../prisma-xs/helpers/bom-mapper.helper';
import { getUserFederatedId, mapToAddToAnalyticsUser } from '../../home/helpers/user.helper';
import { WhiService } from './whi.service';
import { ShowWhiButtonPipe } from '../../ui/pipes/show-whi-button.pipe';
import { SEARCH_ANALYTICS_DEBOUNCE_MS } from '../constants/app.constant';

@Injectable({
  providedIn: 'root'
})
export class DataLayerService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private window: any;
  private readonly searchActionSubject$ = new Subject<DebouncedSearchPayload>();

  constructor(private windowRef: WindowReferenceService,
              private projectManagementService: ProjectManagementService,
              private whiService : WhiService,
              private authService: AuthService) {
    this.window = windowRef.nativeWindow;

    this.searchActionSubject$.pipe(
      groupBy(payload => payload.searchCategory),
      mergeMap(group$ => group$.pipe(
        debounceTime(SEARCH_ANALYTICS_DEBOUNCE_MS),
        distinctUntilChanged((prev, curr) =>
          prev.searchText === curr.searchText &&
          prev.results === curr.results
        )
      ))
    ).subscribe(({ searchText, results, searchCategory }) => {
      this.pushSearchAction(searchText, results, searchCategory);
    });
  }

  private navigateToHomeRoute(currentRoute: string) {
    return currentRoute === '/' || currentRoute.startsWith('/home')
  }

  pushPageView(currentRoute: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    const event = new PageViewAnalytics(currentRoute);
    event.addFromWebView(new ShowWhiButtonPipe(this.whiService).transform(true));
    this.authService.user$.pipe(take(1)).subscribe(user => {
      event.addUserId(getUserFederatedId(user));

      if (this.navigateToHomeRoute(currentRoute)) {
        this.window.dataLayer.push(event.getParameters());
        return;
      }

      this.projectManagementService.getCurrentProjectId().subscribe(projectId => {
        this.window.dataLayer.push(event.addProjectId(projectId));
      });
    });
  }

  pushSearchAction(searchText: string, results: number, searchCategory: SITE_SEARCH_CATEGORY) {
    if (!searchText || !window || !this.window.dataLayer) {
      return;
    }

    this.window.dataLayer.push(new AddToSearch(searchText, results, searchCategory).getParameters());
  }

  pushSearchActionDebounced(searchText: string, results: number, searchCategory: SITE_SEARCH_CATEGORY) {
    this.searchActionSubject$.next({ searchText, results, searchCategory });
  }

  pushSearchResultClickedAction(searchText: string,
                                searchResultPosition: number,
                                siteSearchCategory: SITE_SEARCH_CATEGORY,
                                searchPagePosition = 1) {
    if (!window || !this.window.dataLayer) {
      return;
    }

    this.window.dataLayer.push(
      new AddToSearchResultClicked(searchText, searchResultPosition, siteSearchCategory, searchPagePosition).getParameters());
  }

  pushCtaAction(link: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new AddToCta(link);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushCtaOpenLinkAction(link: string, location: string, ctaName: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new AddToCtaExternalLink(link, location, ctaName);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushCtaOnEnclosureActionTrigger(link: string, location: string, ctaName: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new AddToCtaOnEnclosureActionTrigger(link, location, ctaName);
      event.addUserId(getUserFederatedId(user));

      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushCtaOnProjectActionTrigger(link: string, location: string, ctaName: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new AddToCtaOnProjectActionTrigger(link, location, ctaName);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushDownloadPdfAction(documentNameFull: string, documentId: number, documentNameShort: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new DownloadAnalyticsPdfFile(documentNameFull, documentId, documentNameShort);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushDownloadExcelAction(documentNameFull: string, documentId: number, documentNameShort: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new DownloadAnalyticsExcelFile(documentNameFull, documentId, documentNameShort);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushDownloadWordAction(documentNameFull: string, documentId: number, documentNameShort: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new DownloadAnalyticsWordFile(documentNameFull, documentId, documentNameShort);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  pushDownloadZipAction(documentNameFull: string, documentId: number, documentNameShort: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.authService.user$.pipe(take(1)).subscribe(user => {
      const event = new DownloadAnalyticsZipFile(documentNameFull, documentId, documentNameShort);
      event.addUserId(getUserFederatedId(user));
      this.window.dataLayer.push(event.getParameters());
    });
  }

  private pushNullEcommerceObject() {
    this.window.dataLayer.push({ecommerce:null});
  }

  pushAddToCart(copiedModels: IBomModel[], price: number, action: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.projectManagementService.getCurrentProjectId().pipe(take(1)).subscribe(projectId => {
      this.authService.user$.pipe(take(1)).subscribe(user => {

        const addToCart = new AddToCartAnalytics(
          projectId,
          action,
          { value: price, items: mapToEcommerceItems(copiedModels, projectId) }
        );
        addToCart.addUserId(getUserFederatedId(user));
        this.pushNullEcommerceObject();
        this.window.dataLayer.push(addToCart.getParameters());
      });
    });
  }

  pushViewItemList(copiedModels: IBomModel[], price: number, itemListName: string, itemListOption: string) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    this.projectManagementService.getCurrentProjectId().pipe(take(1)).subscribe(projectId => {
      this.authService.user$.pipe(take(1)).subscribe(user => {
        const viewItemList = new ViewItemListAnalytics(
          getUserFederatedId(user),
          projectId,
          {
            value: price,
            items: mapToEcommerceItems(copiedModels, projectId)
          },
          itemListName,
          itemListOption)

        this.pushNullEcommerceObject();
        this.window.dataLayer.push(viewItemList.getParameters());
      });
    });
  }

  pushLoginAction(user: Auth0User) {
    if (!window || !this.window.dataLayer) {
      return;
    }
    const params = new AddToLoginAnalytics(mapToAddToAnalyticsUser(user)).getParameters();
    this.window.dataLayer.push(params);
  }

  pushLoginActionToDataLayerOnReload() {
    if (!window || !this.window.dataLayer) {
      return;
    }

    sessionStorage.setItem('gaAction', 'initialLogin');
  }

  triggerPushLoginToDataLayerOnReload(): boolean {
    if (!window || !this.window.dataLayer) {
      return;
    }

    if (sessionStorage.getItem('gaAction') !== 'initialLogin') {
      return false;
    }

    sessionStorage.removeItem('gaAction');

    return true;
  }
}
